import { useCallback } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { BomNode } from "../job-tracking/queries/tracking-data/bom-hierarchy-generator";
import {
  PriorityUpsertDetails,
  UpdatePurchasingPriorityMutation,
} from "../job-tracking/queries/tracking-data/useUpdatePurchasingPriorityMutation";

/**
 * Component that renders a button to create job-specific purchasing priority overrides for a root BOM node.
 *
 * This operation will create a new job-specific purchasing priority record for the node at it's current priority level,
 * and new job-specific records for any descendants to match any existing template priority records. The end result is
 * that the priority levels do not change for any node, but they all switch to being job-specific.
 */
export const CreateJobSpecificPurchasingPriorityOverridesButton: React.FC<{
  bomNode: BomNode;
  updatePurchasingPriorityMutation: UpdatePurchasingPriorityMutation;
  className: string;
}> = ({ bomNode, updatePurchasingPriorityMutation, className }) => {
  // Define the click handler for the button that creates job-specific overrides
  const handleClick = useCallback(() => {
    // Define the upsert mutation details for the current node
    const currentNodeMutation: PriorityUpsertDetails = {
      path: bomNode.path,
      jobSpecific: true,
      priority: bomNode.purchasingPriority.priority,
      recordToUpdate: bomNode.purchasingPriority.nodeJobRecord, // Should always be null, but included for consistency
    };

    // Get a list of all child nodes that have template priority records
    const templatePriorityRecords = getTemplatePriorityNodes(bomNode.children);

    // Create upsert mutation details for each child node to create a new job-specific record to match the template priority
    const childNodeMutations: PriorityUpsertDetails[] = templatePriorityRecords.map((node) => ({
      path: node.path,
      jobSpecific: true,
      priority: node.purchasingPriority.priority,
      recordToUpdate: node.purchasingPriority.nodeJobRecord, // Should always be null, but included for consistency
    }));

    // Upsert all the mutation records
    updatePurchasingPriorityMutation.mutate({
      action: "upsert",
      upsertDetails: [currentNodeMutation, ...childNodeMutations],
    });
  }, [bomNode, updatePurchasingPriorityMutation]);

  // Do not render anything if this is not a top-level node, or if this node already has a job-specific priority
  if (bomNode.recursiveDepth !== 0) return null;
  if (bomNode.purchasingPriority.appliedRecordSource === "job") return null;

  return (
    <OverlayTrigger overlay={<Tooltip>Override For This Job</Tooltip>}>
      <Button
        variant="link"
        size="sm"
        disabled={updatePurchasingPriorityMutation.isPending}
        onClick={handleClick}
        className={className}
      >
        <Icon icon="pencil-square" className="text-primary" />
      </Button>
    </OverlayTrigger>
  );
};

/** Recursively finds all child nodes that have a template purchasing priority record */
function getTemplatePriorityNodes(bomNodes: BomNode[]): BomNode[] {
  return bomNodes
    .filter((node) => node.purchasingPriority.nodeTemplateRecord)
    .concat(bomNodes.flatMap((node) => getTemplatePriorityNodes(node.children)));
}
